import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import React, { useEffect, useState } from "react";
import "react-phone-input-2/lib/bootstrap.css";
import { useNavigate } from "react-router-dom";
import countryList from "react-select-country-list";
import { ReactComponent as DownArrow } from "../../../../../assets/AccountingModule/arrow_down.svg";
import DropdownIcon from "../../../../../assets/AccountingModule/drop_down_icon.svg";
import EditIcon from "../../../../../assets/AccountingModule/edit.svg";
import { CustomizedRadio } from "../../../../../components/CustomRadio";
import color from "../../../../../constants/color";
import { addInvoice, paymentTermsOptions } from "../../../../../constants/data";
import { useForm } from "../../../../../hooks/useForm";
import { getDueDateFromPaymentTerm } from "../../../../../utils/Utilities";
import CustomInput from "../../../.././../components/CustomInput";
import style from "./style";
dayjs.extend(utc);
dayjs.extend(timezone);
import { addInvoiceValidation } from "../../../../../helper/Validation";

const CreateInvoice = ({
  itemList,
  handleAddNewInvoice,
  handleItemList,
  customerList,
  handleCustomer,
  loading,
}) => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState();
  const options = countryList().getData();
  const [invoiceTypeButton, setInvoiceTypeButton] =
    useState("One Time Invoice");
  const [profileName, setProfileName] = useState("");
  const [repeatEvery, setRepeatEvery] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(dayjs());
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState();
  const [penaltyAmount, setPenaltyAmount] = useState("");
  const [otherInfo, setOtherInfo] = useState("");

  useEffect(() => {
    if (window.intlTelInputGlobals) {
      window.intlTelInputGlobals.loadUtils("build/js/utils.js");
    }
  }, []);

  // Add useEffect to populate addresses when customer is selected
  useEffect(() => {
    if (customer) {
      // Populate billing address
      if (customer.billingAddress) {
        setBillingCountry(customer.billingAddress.country || "");
        setBillingStreet1(customer.billingAddress.street1 || "");
        setBillingStreet2(customer.billingAddress.street2 || "");
        setBillingCity(customer.billingAddress.city || "");
        setBillingState(customer.billingAddress.state || "");
        setBillingPinCode(customer.billingAddress.pinCode || "");
        setBillingPhone(customer.billingAddress.phone || "");
      } else {
        // Clear billing address if customer doesn't have billing address
        setBillingCountry("");
        setBillingStreet1("");
        setBillingStreet2("");
        setBillingCity("");
        setBillingState("");
        setBillingPinCode("");
        setBillingPhone("");
      }

      // Populate shipping address
      if (customer.shippingAddress) {
        setShippingCountry(customer.shippingAddress.country || "");
        setShippingStreet1(customer.shippingAddress.street1 || "");
        setShippingStreet2(customer.shippingAddress.street2 || "");
        setShippingCity(customer.shippingAddress.city || "");
        setShippingState(customer.shippingAddress.state || "");
        setShippingPinCode(customer.shippingAddress.pinCode || "");
        setShippingPhone(customer.shippingAddress.phone || "");
      } else {
        // Clear shipping address if customer doesn't have shipping address
        setShippingCountry("");
        setShippingStreet1("");
        setShippingStreet2("");
        setShippingCity("");
        setShippingState("");
        setShippingPinCode("");
        setShippingPhone("");
      }
    } else {
      // Clear all addresses if no customer is selected
      clearAddresses();
    }
  }, [customer]);

  const [product, setProduct] = useState([
    { name: "", productId: "", desc: "", unit: "", quantity: "", amount: "" },
  ]);
  const [totalAmount, setTotalAmount] = useState("0");

  const [invoiceData, setInvoiceData] = useState(addInvoice());
  const [paymentTerms, setPaymentTerms] = useState();
  const [invoiceType, setInvoiceType] = useState();

  // Billing Address
  const [billingCountry, setBillingCountry] = useState("");
  const [billingStreet1, setBillingStreet1] = useState("");
  const [billingStreet2, setBillingStreet2] = useState("");
  const [billingCity, setBillingCity] = useState("");
  const [billingState, setBillingState] = useState("");
  const [billingPinCode, setBillingPinCode] = useState("");
  const [billingPhone, setBillingPhone] = useState("");

  // Shipping Address
  const [shippingCountry, setShippingCountry] = useState("");
  const [shippingStreet1, setShippingStreet1] = useState("");
  const [shippingStreet2, setShippingStreet2] = useState("");
  const [shippingCity, setShippingCity] = useState("");
  const [shippingState, setShippingState] = useState("");
  const [shippingPinCode, setShippingPinCode] = useState("");
  const [shippingPhone, setShippingPhone] = useState("");

  const handleCopyBillingToShipping = () => {
    setShippingCountry(billingCountry);
    setShippingStreet1(billingStreet1);
    setShippingStreet2(billingStreet2);
    setShippingCity(billingCity);
    setShippingState(billingState);
    setShippingPinCode(billingPinCode);
    setShippingPhone(billingPhone);
  };

  const clearAddresses = () => {
    // Clear billing address
    setBillingCountry("");
    setBillingStreet1("");
    setBillingStreet2("");
    setBillingCity("");
    setBillingState("");
    setBillingPinCode("");
    setBillingPhone("");

    // Clear shipping address
    setShippingCountry("");
    setShippingStreet1("");
    setShippingStreet2("");
    setShippingCity("");
    setShippingState("");
    setShippingPinCode("");
    setShippingPhone("");
  };

  // Helper function to check if addresses are populated from customer
  const isAddressPopulatedFromCustomer = () => {
    return (
      customer &&
      ((customer.billingAddress &&
        Object.values(customer.billingAddress).some((val) => val)) ||
        (customer.shippingAddress &&
          Object.values(customer.shippingAddress).some((val) => val)))
    );
  };

  useEffect(() => {
    const updatedData = addInvoice(customer);
    setInvoiceData(updatedData);
  }, [customer]);

  const handleAddField = () => {
    setProduct([
      ...product,
      { name: "", productId: "", desc: "", unit: "", quantity: "", amount: "" },
    ]);
    handleItemList();
  };

  const handleProduct = (value, index, key) => {
    const updatedProjectDetails = [...product];

    if ((key === "quantity" || key === "unit") && Number(value) < 0) {
      return;
    }

    if (key === "invoiceQuantity" || key === "unit") {
      const qty =
        key === "invoiceQuantity"
          ? Number(value)
          : Number(updatedProjectDetails[index].quantity);
      const unitCost =
        key === "unit"
          ? Number(value)
          : Number(updatedProjectDetails[index].unit);
      updatedProjectDetails[index]["amount"] = qty * unitCost;
      calculateTotalAmount();
    }

    if (key === "name") {
      const selectedProject = itemList.find((proj) => proj.title === value);
      if (selectedProject) {
        updatedProjectDetails[index].productId = selectedProject
          ? selectedProject?._id
          : "";
        updatedProjectDetails[index].unit = Number(
          selectedProject.sellingPrice || 0
        );
        updatedProjectDetails[index].quantity = Number(
          selectedProject?.quantity || 0
        );
        updatedProjectDetails[index].desc = selectedProject?.desc || "";
        updatedProjectDetails[index].amount = 0;
        calculateTotalAmount();
      }
    }

    if (key === "quantity") {
      updatedProjectDetails[index][key] = Number(value);
    } else if (key === "unit") {
      updatedProjectDetails[index][key] = Number(value);
    } else {
      updatedProjectDetails[index][key] = value;
    }
    setProduct(updatedProjectDetails);
  };

  const handleDeleteField = (index) => {
    const updatedProductInformation = [...product];
    updatedProductInformation.splice(index, 1);
    setProduct(updatedProductInformation);
  };

  const calculateFinalAmount = (totalAmount, discountPercent, taxPercent) => {
    const discountedAmount = totalAmount * (1 - (discountPercent || 0) / 100);
    const discountValue = totalAmount - discountedAmount;
    const taxAmount = discountedAmount * ((taxPercent || 0) / 100);
    const finalAmount = discountedAmount + taxAmount;

    return {
      finalAmount: finalAmount.toFixed(2),
      discountValue: discountValue.toFixed(2),
      taxAmount: taxAmount.toFixed(2),
    };
  };

  const calculateGrandTotalAmount = (
    totalAmount,
    discountPercent,
    taxPercent
  ) => {
    const discountedAmount = totalAmount * (1 - discountPercent / 100);
    const taxAmount = discountedAmount * (taxPercent / 100);
    let finalAmount = discountedAmount + taxAmount;
    return (finalAmount = finalAmount.toFixed(2));
  };

  const handleCreateInvoice = async (data) => {
    const grandTotal = calculateGrandTotalAmount(
      totalAmount,
      discount?.value,
      tax?.value
    );

    const { discountValue, taxAmount } = calculateFinalAmount(
      Number(totalAmount),
      Number(discount?.value),
      Number(tax?.value)
    );

    const billingAddress = {
      country: billingCountry,
      street1: billingStreet1,
      street2: billingStreet2,
      city: billingCity,
      state: billingState,
      pinCode: billingPinCode,
      phone: billingPhone,
    };

    const shippingAddress = {
      country: shippingCountry,
      street1: shippingStreet1,
      street2: shippingStreet2,
      city: shippingCity,
      state: shippingState,
      pinCode: shippingPinCode,
      phone: shippingPhone,
    };

    const productInformation = product.map((item) => {
      return {
        name: item.name,
        productId: item.productId,
        description: item.desc,
        unitCost: item.unit,
        quantity: item.invoiceQuantity,
        amount: item.amount,
      };
    });

    const finalData = {
      customer: customer?._id,
      billingAddress: billingAddress,
      shippingAddress: shippingAddress,
      paymentTerms: paymentTerms,
      dailyPenaltyAmount: penaltyAmount,
      otherInformation: otherInfo,
      discountPercentage: Number(discount?.value ?? 0),
      discountValue: Number(discountValue),
      taxPercentage: Number(tax?.value ?? 0),
      taxValue: Number(taxAmount),
      total: Number(totalAmount),
      grandTotal: Number(grandTotal),
      productInformation: productInformation,
    };

    if (invoiceTypeButton === "One Time Invoice") {
      finalData.invoiceType = "One-Time";
      finalData.invoiceDate = invoiceDate.utc().startOf("day").toISOString();
      finalData.dueDate = endDate.utc().endOf("end").toISOString();
    } else if (invoiceTypeButton === "Recurring Invoice") {
      finalData.invoiceType = "Recurring";
      finalData.profileName = profileName;
      finalData.repeatEvery = repeatEvery;
      finalData.startOn = startDate.utc().startOf("day").toISOString();
      finalData.endOn = endDate.utc().endOf("end").toISOString();
    }

    handleAddNewInvoice(finalData);
  };

  const calculateTotalAmount = () => {
    let total = 0;
    product.forEach((item) => {
      total += Number(item.amount);
    });
    return setTotalAmount(total);
  };

  const { state, handleChange, handleSubmit, handleFocus } = useForm(
    {
      ...invoiceData,
      profileName: { value: "", error: "", focus: null },
      repeatEvery: { value: "", error: "", focus: null },
      startDate: { value: dayjs(), error: "", focus: null },
      dailyPenaltyAmount: { value: "", error: "", focus: null },
      otherInformation: { value: "", error: "", focus: null },
    },
    (data) => addInvoiceValidation(data, invoiceTypeButton),
    handleCreateInvoice
  );
  const { tax, discount, unitCost } = state;

  const { finalAmount, discountValue, taxAmount } = calculateFinalAmount(
    Number(totalAmount),
    Number(discount?.value),
    Number(tax?.value)
  );

  const CustomDropdownIcon = () => (
    <Box sx={{ display: "flex", alignItems: "center", pr: 2 }}>
      <img src={DropdownIcon} alt="Dropdown" width={11} height={11} />
    </Box>
  );

  const isFormValid = () => {
    // Common required fields for both invoice types
    const commonFieldsValid =
      customer &&
      billingCountry &&
      billingStreet1 &&
      billingCity &&
      billingState &&
      billingPinCode &&
      billingPhone &&
      shippingCountry &&
      shippingStreet1 &&
      shippingCity &&
      shippingState &&
      shippingPinCode &&
      shippingPhone &&
      paymentTerms &&
      product.length > 0 &&
      !product.every(
        (p) =>
          !p.name ||
          !p.desc ||
          !p.unit === undefined ||
          !p.quantity === undefined ||
          !p.amount === undefined ||
          p.quantity <= 0 ||
          p.unit <= 0
      );

    if (invoiceTypeButton === "One Time Invoice") {
      return commonFieldsValid && invoiceDate && endDate;
    } else {
      return (
        commonFieldsValid && profileName && repeatEvery && startDate && endDate
      );
    }
  };

  const handlePaymentTermSelection = (value) => {
    setPaymentTerms(value);

    let dueDate;
    if (invoiceTypeButton === "One Time Invoice") {
      dueDate = getDueDateFromPaymentTerm(value, invoiceDate);
    } else if (invoiceTypeButton === "Recurring Invoice") {
      dueDate = getDueDateFromPaymentTerm(value, startDate);
    }

    if (dueDate?.isValid()) {
      setEndDate(dueDate.endOf("day"));
    }
  };

  return (
    <>
      <Box px="15px" mt={2}>
        <Box sx={style.headerContainer}>
          <Typography sx={style.leaveHeading}>Create Invoice</Typography>
        </Box>
      </Box>
      <Box sx={{ ...style.itemSection, marginTop: 1 }}>
        <Box sx={{ backgroundColor: "#28AA4A", width: "2%", height: 2 }}></Box>
        <Typography sx={style.gridText}>Invoice Details</Typography>
      </Box>
      <form onSubmit={(e) => handleSubmit(e)}>
        <Grid container sx={style.mainContainer}>
          {/* --- Invoice Details */}
          <Grid item xs={12} lg={11.8} sx={style.gridbox}>
            {/* Radio Button */}
            <Box sx={{ ...style.inputSection, mb: 2 }}>
              <Box sx={{ ...style.inputBox, display: "flex", gap: 2 }}>
                <CustomizedRadio
                  handleChange={setInvoiceTypeButton}
                  value={invoiceTypeButton}
                  defaultChecked={invoiceTypeButton}
                  radioLabel={[
                    { label: "One Time Invoice", value: "One Time Invoice" },
                    { label: "Recurring Invoice", value: "Recurring Invoice" },
                  ]}
                />
              </Box>
            </Box>

            {/* Customer */}
            <Box sx={style.inputSection}>
              <Box sx={{ ...style.inputBox, width: "100%" }}>
                <Autocomplete
                  id="autocomplete"
                  options={[
                    { _id: "add_new", name: "+ New Customer" },
                    ...(customerList || []),
                  ]}
                  getOptionLabel={(option) => option?.name || ""}
                  onInputChange={(_, newInputValue) => {
                    handleCustomer(newInputValue);
                  }}
                  onChange={(_, newValue) => {
                    if (newValue?._id === "add_new") {
                      navigate("/dashboard/addNewCustomer"); // redirect page
                      return;
                    }
                    setCustomer(newValue);

                    // Clear addresses if no customer is selected
                    if (!newValue) {
                      clearAddresses();
                      return;
                    }

                    // Populate addresses immediately when customer is selected
                    if (newValue) {
                      // Populate billing address
                      if (newValue.billingAddress) {
                        setBillingCountry(
                          newValue.billingAddress.country || ""
                        );
                        setBillingStreet1(
                          newValue.billingAddress.street1 || ""
                        );
                        setBillingStreet2(
                          newValue.billingAddress.street2 || ""
                        );
                        setBillingCity(newValue.billingAddress.city || "");
                        setBillingState(newValue.billingAddress.state || "");
                        setBillingPinCode(
                          newValue.billingAddress.pinCode || ""
                        );
                        setBillingPhone(newValue.billingAddress.phone || "");
                      } else {
                        // Clear billing address if customer doesn't have billing address
                        setBillingCountry("");
                        setBillingStreet1("");
                        setBillingStreet2("");
                        setBillingCity("");
                        setBillingState("");
                        setBillingPinCode("");
                        setBillingPhone("");
                      }

                      // Populate shipping address
                      if (newValue.shippingAddress) {
                        setShippingCountry(
                          newValue.shippingAddress.country || ""
                        );
                        setShippingStreet1(
                          newValue.shippingAddress.street1 || ""
                        );
                        setShippingStreet2(
                          newValue.shippingAddress.street2 || ""
                        );
                        setShippingCity(newValue.shippingAddress.city || "");
                        setShippingState(newValue.shippingAddress.state || "");
                        setShippingPinCode(
                          newValue.shippingAddress.pinCode || ""
                        );
                        setShippingPhone(newValue.shippingAddress.phone || "");
                      } else {
                        // Clear shipping address if customer doesn't have shipping address
                        setShippingCountry("");
                        setShippingStreet1("");
                        setShippingStreet2("");
                        setShippingCity("");
                        setShippingState("");
                        setShippingPinCode("");
                        setShippingPhone("");
                      }
                    }
                  }}
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      style={
                        option._id === "add_new"
                          ? { color: color.secondary }
                          : {}
                      }
                    >
                      {option.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <>
                      <InputLabel sx={style.inputText}>
                        Customer
                        <span style={{ color: "red" }}>*</span>
                      </InputLabel>
                      <TextField
                        {...params}
                        sx={{ ...style.modalInput, ...style.inputSpace }}
                        placeholder="Select Customer"
                        variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    </>
                  )}
                />
              </Box>
            </Box>

            {/* Billing and Shipping Address */}
            <Grid container>
              <Grid
                item
                xs={12}
                lg={11.7}
                sx={{
                  ...style.gridbox,
                  py: 0,
                  px: { xs: 0, sm: 0 },
                  borderRadius: 0,
                  mt: 1,
                }}
              >
                <Box sx={style.addressBox}>
                  <Box sx={{ width: { xs: "100%", lg: "50%" } }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontWeight: "bold" }}>
                        Billing Address
                      </Typography>
                      <img src={EditIcon} height={16} width={16} alt="edit" />
                    </Box>

                    <Box sx={style.addressInput}>
                      <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                        <Typography sx={style.inputText}>
                          Country/Region
                        </Typography>
                      </Box>
                      <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                        <FormControl
                          fullWidth
                          required
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              height: "2.8em",
                              marginTop: { xs: "0em", md: "0.5em" },
                              borderRadius: "10px",
                            },
                          }}
                        >
                          <Select
                            value={billingCountry || ""}
                            onChange={(event) =>
                              setBillingCountry(event.target.value)
                            }
                            name="billingCountry"
                            sx={style.dropDownMenu}
                            onFocus={handleFocus}
                            displayEmpty
                          >
                            <MenuItem disabled value="">
                              Select
                            </MenuItem>
                            {options.map((country, index) => (
                              <MenuItem
                                key={index}
                                sx={style.dropDownMenu}
                                value={country.label}
                              >
                                {country.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        justifyContent: "space-between",
                      }}
                    >
                      <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                        <Typography sx={style.inputText}>Address</Typography>
                      </Box>
                      <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                        <TextField
                          variant="outlined"
                          required
                          fullWidth
                          placeholder="Street 1"
                          value={billingStreet1}
                          onChange={(e) => setBillingStreet1(e.target.value)}
                          error={false}
                          onFocus={handleFocus}
                          autoComplete="bstreet1"
                          sx={{ ...style.addressTextField, mb: 3 }}
                          InputProps={{
                            sx: {
                              fontSize: "14px",
                              fontWeight: 400,
                            },
                          }}
                        />
                        <TextField
                          variant="outlined"
                          fullWidth
                          placeholder="Street 2"
                          value={billingStreet2}
                          onChange={(e) => setBillingStreet2(e.target.value)}
                          error={false}
                          onFocus={handleFocus}
                          autoComplete="bstreet2"
                          sx={style.addressTextField}
                          InputProps={{
                            sx: {
                              fontSize: "14px",
                              fontWeight: 400,
                            },
                          }}
                        />
                      </Box>
                    </Box>

                    <Box sx={style.addressInput}>
                      <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                        <Typography sx={style.inputText}>City</Typography>
                      </Box>
                      <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                        <TextField
                          variant="outlined"
                          required
                          fullWidth
                          placeholder="City"
                          value={billingCity}
                          onChange={(e) => setBillingCity(e.target.value)}
                          error={false}
                          onFocus={handleFocus}
                          autoComplete="bcity"
                          sx={style.addressTextField}
                          InputProps={{
                            sx: {
                              fontSize: "14px",
                              fontWeight: 400,
                            },
                          }}
                        />
                      </Box>
                    </Box>

                    <Box sx={style.addressInput}>
                      <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                        <Typography sx={style.inputText}>State</Typography>
                      </Box>
                      <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                        <TextField
                          variant="outlined"
                          required
                          fullWidth
                          placeholder="State"
                          value={billingState}
                          onChange={(e) => setBillingState(e.target.value)}
                          error={false}
                          onFocus={handleFocus}
                          autoComplete="bstate"
                          sx={style.addressTextField}
                        />
                      </Box>
                    </Box>
                    <Box sx={style.addressInput}>
                      <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                        <Typography sx={style.inputText}>Pin Code</Typography>
                      </Box>
                      <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                        <TextField
                          variant="outlined"
                          required
                          fullWidth
                          placeholder="Pin Code"
                          value={billingPinCode}
                          onChange={(e) => setBillingPinCode(e.target.value)}
                          error={false}
                          onFocus={handleFocus}
                          autoComplete="bpinCode"
                          sx={style.addressTextField}
                          InputProps={{
                            sx: {
                              fontSize: "14px",
                              fontWeight: 400,
                            },
                          }}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ ...style.addressInput, mb: 0 }}>
                      <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                        <Typography sx={style.inputText}>Phone</Typography>
                      </Box>
                      <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                        <TextField
                          variant="outlined"
                          required
                          fullWidth
                          placeholder="Phone"
                          value={billingPhone}
                          onChange={(e) => setBillingPhone(e.target.value)}
                          error={false}
                          onFocus={handleFocus}
                          autoComplete="bphone"
                          sx={style.addressTextField}
                          InputProps={{
                            sx: {
                              fontSize: "14px",
                              fontWeight: 400,
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ width: { xs: "100%", lg: "50%" } }}>
                    <Box sx={{ display: "flex", gap: "8px" }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography sx={{ fontWeight: "bold" }}>
                          Shipping Address
                        </Typography>
                        <img src={EditIcon} height={16} width={16} alt="edit" />
                      </Box>
                      <Typography
                        sx={{ color: color.secondary, cursor: "pointer" }}
                        onClick={handleCopyBillingToShipping}
                      >
                        {"( "}
                        <DownArrow /> Copy Billing Address{" )"}
                      </Typography>
                    </Box>
                    <Box sx={style.addressInput}>
                      <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                        <Typography sx={style.inputText}>
                          Country/Region
                        </Typography>
                      </Box>
                      <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                        <FormControl
                          fullWidth
                          required
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              height: "2.8em",
                              marginTop: "0.5em",
                              borderRadius: "10px",
                            },
                          }}
                        >
                          <Select
                            value={shippingCountry || ""}
                            onChange={(event) =>
                              setShippingCountry(event.target.value)
                            }
                            name="shippingCountry"
                            sx={style.dropDownMenu}
                            onFocus={handleFocus}
                            renderValue={(selected) => {
                              if (selected === "") {
                                return <em>Select</em>;
                              }
                              return selected;
                            }}
                          >
                            <MenuItem disabled value="">
                              Select
                            </MenuItem>
                            {options.map((country, index) => (
                              <MenuItem
                                key={index}
                                sx={style.dropDownMenu}
                                value={country.label}
                              >
                                {country.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        justifyContent: "space-between",
                      }}
                    >
                      <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                        <Typography sx={style.inputText}>Address</Typography>
                      </Box>
                      <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                        <TextField
                          variant="outlined"
                          required
                          fullWidth
                          placeholder="Street 1"
                          value={shippingStreet1}
                          onChange={(e) => setShippingStreet1(e.target.value)}
                          error={false}
                          onFocus={handleFocus}
                          autoComplete="sstreet1"
                          sx={{ ...style.addressTextField, mb: 3 }}
                          InputProps={{
                            sx: {
                              fontSize: "14px",
                              fontWeight: 400,
                            },
                          }}
                        />
                        <TextField
                          variant="outlined"
                          fullWidth
                          placeholder="Street 2"
                          value={shippingStreet2}
                          onChange={(e) => setShippingStreet2(e.target.value)}
                          error={false}
                          onFocus={handleFocus}
                          autoComplete="sstreet2"
                          sx={style.addressTextField}
                          InputProps={{
                            sx: {
                              fontSize: "14px",
                              fontWeight: 400,
                            },
                          }}
                        />
                      </Box>
                    </Box>
                    <Box sx={style.addressInput}>
                      <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                        <Typography sx={style.inputText}>City</Typography>
                      </Box>
                      <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                        <TextField
                          variant="outlined"
                          required
                          fullWidth
                          placeholder="City"
                          value={shippingCity}
                          onChange={(e) => setShippingCity(e.target.value)}
                          error={false}
                          onFocus={handleFocus}
                          autoComplete="scity"
                          sx={style.addressTextField}
                          InputProps={{
                            sx: {
                              fontSize: "14px",
                              fontWeight: 400,
                            },
                          }}
                        />
                      </Box>
                    </Box>
                    <Box sx={style.addressInput}>
                      <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                        <Typography sx={style.inputText}>State</Typography>
                      </Box>
                      <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                        <TextField
                          variant="outlined"
                          required
                          fullWidth
                          placeholder="State"
                          value={shippingState}
                          onChange={(e) => setShippingState(e.target.value)}
                          error={false}
                          onFocus={handleFocus}
                          autoComplete="sstate"
                          sx={style.addressTextField}
                        />
                      </Box>
                    </Box>
                    <Box sx={style.addressInput}>
                      <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                        <Typography sx={style.inputText}>Pin Code</Typography>
                      </Box>
                      <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                        <TextField
                          variant="outlined"
                          required
                          fullWidth
                          placeholder="Pin Code"
                          value={shippingPinCode}
                          onChange={(e) => setShippingPinCode(e.target.value)}
                          error={false}
                          onFocus={handleFocus}
                          autoComplete="spinCode"
                          sx={style.addressTextField}
                          InputProps={{
                            sx: {
                              fontSize: "14px",
                              fontWeight: 400,
                            },
                          }}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ ...style.addressInput, mb: 0 }}>
                      <Box sx={{ width: { xs: "100%", md: "20%" } }}>
                        <Typography sx={style.inputText}>Phone</Typography>
                      </Box>
                      <Box sx={{ width: { xs: "100%", md: "75%" } }}>
                        <TextField
                          variant="outlined"
                          required
                          fullWidth
                          placeholder="Phone"
                          value={shippingPhone}
                          onChange={(e) => setShippingPhone(e.target.value)}
                          error={false}
                          onFocus={handleFocus}
                          autoComplete="sphone"
                          sx={style.addressTextField}
                          InputProps={{
                            sx: {
                              fontSize: "14px",
                              fontWeight: 400,
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {/* Invoice Date, Payment Terms, End Date */}
            {invoiceTypeButton === "Recurring Invoice" ? (
              <Box sx={style.inputSection}>
                <Box sx={style.inputBox}>
                  <FormControl
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "2.5em",
                        marginTop: "0.5em",
                        borderRadius: "10px",
                      },
                    }}
                  >
                    <Typography sx={style.inputText}>
                      Profile Name<span style={{ color: "red" }}>*</span>
                    </Typography>
                    <TextField
                      variant="outlined"
                      required
                      fullWidth
                      size="small"
                      placeholder="Enter Profile Name"
                      value={profileName}
                      onChange={(e) => setProfileName(e.target.value)}
                      sx={{
                        ...style.addressTextField,
                        // height: "2.5em",
                        // marginTop: "0.5em",
                        borderRadius: "10px",
                      }}
                    />
                  </FormControl>
                </Box>

                <Box sx={style.inputBox}>
                  <FormControl
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "2.5em",
                        marginTop: "0.5em",
                        borderRadius: "10px",
                      },
                    }}
                  >
                    <Typography sx={style.inputText}>
                      Repeat Every<span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Select
                      onChange={(event) => setRepeatEvery(event.target.value)}
                      name="repeatEvery"
                      value={repeatEvery || ""}
                      displayEmpty
                      IconComponent={CustomDropdownIcon}
                    >
                      <MenuItem disabled value="">
                        Select
                      </MenuItem>
                      <MenuItem value="Daily">Daily</MenuItem>
                      <MenuItem value="Weekly">Weekly</MenuItem>
                      <MenuItem value="Monthly">Monthly</MenuItem>
                      <MenuItem value="Quarterly">Quarterly</MenuItem>
                      <MenuItem value="Yearly">Yearly</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={style.inputBox}>
                  <FormControl
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "2.5em",
                        marginTop: "0.5em",
                        borderRadius: "10px",
                      },
                    }}
                  >
                    <Typography sx={style.inputText}>
                      Payment Terms<span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Select
                      onChange={(event) =>
                        handlePaymentTermSelection(event.target.value)
                      }
                      name="paymentTerms"
                      value={paymentTerms || ""}
                      displayEmpty
                      renderValue={(selected) =>
                        selected ? (
                          selected
                        ) : (
                          <span style={{ color: "#999" }}>
                            Select Payment Terms
                          </span>
                        )
                      }
                    >
                      <MenuItem disabled value="">
                        Select Payment Terms
                      </MenuItem>
                      {paymentTermsOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            ) : (
              <Box sx={style.inputSection}>
                <Box sx={style.inputBox}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <InputLabel sx={style.label}>Invoice Date</InputLabel>
                    <DatePicker
                      value={invoiceDate}
                      onChange={(newValue) => {
                        setInvoiceDate(newValue);
                      }}
                      sx={style.dateInput}
                    />
                  </LocalizationProvider>
                </Box>

                <Box sx={style.inputBox}>
                  <FormControl
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "2.5em",
                        marginTop: "0.4em",
                        borderRadius: "10px",
                      },
                    }}
                  >
                    <Typography sx={style.inputText}>
                      Payment Terms<span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Select
                      onChange={(event) =>
                        handlePaymentTermSelection(event.target.value)
                      }
                      name="paymentTerms"
                      value={paymentTerms || ""}
                      displayEmpty
                      renderValue={(selected) =>
                        selected ? (
                          selected
                        ) : (
                          <span style={{ color: "#999" }}>
                            Select Payment Terms
                          </span>
                        )
                      }
                    >
                      {paymentTermsOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ ...style.inputBox }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <InputLabel sx={style.label}>
                      Due Date<span style={{ color: "red" }}>*</span>
                    </InputLabel>
                    <DatePicker
                      value={endDate}
                      minDate={invoiceDate}
                      onChange={(newValue) => {
                        setEndDate(newValue.endOf("day"));
                      }}
                      sx={{ ...style.dateInput }}
                      disabled={paymentTerms !== "Custom"}
                    />
                  </LocalizationProvider>
                </Box>
              </Box>
            )}

            {/* Daily Penalty Amount, Other Informations */}
            <Box
              sx={{ ...style.inputSection, justifyContent: "start", gap: 3.5 }}
            >
              {invoiceTypeButton === "Recurring Invoice" ? (
                <>
                  <Grid container spacing={3}>
                    <Grid item xs={12} lg={4}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <InputLabel sx={style.label}>
                          Start on<span style={{ color: "red" }}>*</span>
                        </InputLabel>
                        <DatePicker
                          value={startDate}
                          onChange={(newValue) => {
                            setStartDate(newValue.startOf("day"));
                          }}
                          sx={style.dateInput}
                          slotProps={{
                            field: {
                              readOnly: true,
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12} lg={4}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <InputLabel sx={style.label}>
                          End on<span style={{ color: "red" }}>*</span>
                        </InputLabel>
                        <DatePicker
                          value={endDate}
                          minDate={startDate}
                          onChange={(newValue) => {
                            setEndDate(newValue.endOf("day"));
                          }}
                          sx={style.dateInput}
                          slotProps={{
                            field: {
                              readOnly: true,
                            },
                          }}
                          disabled={paymentTerms !== "Custom"}
                        />
                      </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12} lg={4}>
                      <Typography sx={style.inputText}>
                        Daily Penalty Amount
                        <span style={{ color: "red" }}>*</span>
                      </Typography>
                      <CustomInput
                        variant="outlined"
                        required
                        fullWidth
                        id="penaltyAmount"
                        placeholder="Enter Penalty Amount"
                        error={""}
                        name="penaltyAmount"
                        value={penaltyAmount}
                        onChange={(e, val) => setPenaltyAmount(val)}
                        onFocus={handleFocus}
                      />
                    </Grid>
                  </Grid>
                </>
              ) : (
                <Grid container spacing={3}>
                  <Grid item xs={12} lg={4}>
                    <Box sx={{ ...style.inputBox, width: "100%" }}>
                      <Typography sx={style.inputText}>
                        Daily Penalty Amount
                      </Typography>
                      <CustomInput
                        variant="outlined"
                        required
                        fullWidth
                        id="penaltyAmount"
                        placeholder="Daily Penalty Amount"
                        error={""}
                        name="penaltyAmount"
                        value={penaltyAmount}
                        onChange={(e, val) => setPenaltyAmount(val)}
                        onFocus={handleFocus}
                        autoComplete="penaltyAmount"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} lg={8}>
                    <Box sx={{ ...style.inputBox, width: "100%" }}>
                      <Typography sx={style.inputText}>
                        Other Information
                      </Typography>
                      <CustomInput
                        variant="outlined"
                        required
                        fullWidth
                        id="otherInfo"
                        error={""}
                        placeholder="(Optional)"
                        name="otherInfo"
                        value={otherInfo}
                        onChange={(e, val) => setOtherInfo(val)}
                        onFocus={handleFocus}
                        autoComplete="otherInfo"
                      />
                    </Box>
                  </Grid>
                </Grid>
              )}
            </Box>
            <Box
              sx={{ ...style.inputSection, justifyContent: "start", gap: 3.5 }}
            >
              {invoiceTypeButton === "Recurring Invoice" ? (
                <Grid item xs={12} lg={12}>
                  {/* {invoiceTypeButton === "Recurring Invoice" ? ( */}
                  <Typography sx={style.inputText}>
                    Other Information
                  </Typography>
                  <CustomInput
                    variant="outlined"
                    fullWidth
                    id="otherInfo"
                    placeholder="(Optional)"
                    error={""}
                    name="otherInfo"
                    value={otherInfo}
                    onChange={(e, val) => setOtherInfo(val)}
                    onFocus={handleFocus}
                  />
                </Grid>
              ) : null}
            </Box>
          </Grid>

          {/* --- Product Information */}
          <Box sx={style.itemSection}>
            <Box
              sx={{ backgroundColor: "#28AA4A", width: "2%", height: 2 }}
            ></Box>
            <Typography sx={style.gridText}>
              Product Informations<span style={{ color: "red" }}>*</span>
            </Typography>
          </Box>

          <Grid item xs={12} lg={11.8} sx={style.gridbox}>
            {product.map((item, index) => {
              return (
                <Box sx={style.leaveSection} key={index}>
                  <Grid container sx={style.itemGrid}>
                    <Grid item xs={12} lg={2}>
                      <Autocomplete
                        id="autocomplete"
                        options={[
                          { _id: "add_new", title: "+ New Product" },
                          ...(itemList || []),
                        ]}
                        getOptionLabel={(option) => option?.title}
                        onInputChange={(_, newInputValue) => {
                          handleItemList(newInputValue);
                        }}
                        onChange={(_, newValue) => {
                          if (newValue?._id === "add_new") {
                            navigate("/dashboard/addNewItem"); // redirect page
                            return;
                          }
                          if (!newValue) {
                            handleProduct("", index, "name");
                            handleProduct("", index, "invoiceQuantity");
                            return;
                          }
                          handleProduct(newValue?.title, index, "name");
                        }}
                        renderOption={(props, option) => (
                          <li
                            {...props}
                            style={
                              option._id === "add_new"
                                ? { color: color.secondary }
                                : {}
                            }
                          >
                            {option.title}
                          </li>
                        )}
                        renderInput={(params) => (
                          <>
                            <TextField
                              placeholder="Product Name"
                              {...params}
                              sx={{ ...style.modalInput, ...style.inputSpace }}
                              // label="Search Employee"
                              variant="outlined"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {loading ? (
                                      <CircularProgress
                                        color="inherit"
                                        size={20}
                                      />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          </>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} lg={3}>
                      <CustomInput
                        variant="outlined"
                        required
                        fullWidth
                        id="description"
                        placeholder="Description"
                        defaultValue={product[index]?.desc}
                        error={""}
                        name="desc"
                        value={product[index]?.desc}
                        onChange={(e, val) => handleProduct(val, index, "desc")}
                        // onFocus={handleFocus}
                        autoComplete="desc"
                      />
                    </Grid>
                    <Grid item xs={12} lg={1.9}>
                      <CustomInput
                        variant="outlined"
                        required
                        id="unitCost"
                        placeholder="Unit Cost"
                        error={unitCost?.error}
                        name="unitCost"
                        defaultValue={product[index]?.unit}
                        type="number"
                        value={product[index]?.unit?.toString()}
                        onChange={(e, val) => handleProduct(val, index, "unit")}
                        onFocus={handleFocus}
                        autoComplete="unitCost"
                      />
                    </Grid>
                    <Grid item xs={12} lg={1.9}>
                      <Box
                        display="flex"
                        alignItems="center"
                        sx={{ ...style.customInput, width: "100%" }}
                      >
                        <TextField
                          variant="outlined"
                          value={
                            product[index]?.invoiceQuantity === 0
                              ? 0
                              : product[index]?.invoiceQuantity
                          }
                          type="number"
                          placeholder="Quantity"
                          sx={{
                            width: "100%",
                            paddingRight: "0px !important",
                            "& .MuiOutlinedInput-root": {
                              paddingRight: "0px !important",
                            },
                          }}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            const maxQuantity = product[index]?.quantity;

                            // Check if value is within valid range
                            if (
                              !isNaN(value) &&
                              value >= 0 &&
                              value <= maxQuantity
                            ) {
                              handleProduct(value, index, "invoiceQuantity");
                            } else if (e.target.value === "") {
                              // Allow empty input
                              handleProduct("", index, "invoiceQuantity");
                            } else {
                              // If invalid, set to max quantity or 0
                              const validValue = value < 0 ? 0 : maxQuantity;
                              handleProduct(
                                validValue,
                                index,
                                "invoiceQuantity"
                              );
                            }
                          }}
                          onFocus={handleFocus}
                          InputProps={{
                            inputProps: {
                              min: 0,
                              max: product[index]?.quantity, // Set maximum value
                            },
                            endAdornment: (
                              <InputAdornment position="end" variant="filled">
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    bgcolor: "#DEDEDE",
                                    height: 43,
                                    p: 0,
                                    minWidth: 60, // Ensure minimum width
                                    maxWidth: 120, // Prevent excessive width
                                    ...style.inputBgBox,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      minWidth: 21,
                                      maxWidth: 80, // Limit width to prevent overflow
                                      height: "100%",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontWeight: "600",
                                      userSelect: "none",
                                      borderRadius: "8px",
                                      fontSize: "14px", // Smaller font for long numbers
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      px: 0.5,
                                    }}
                                  >
                                    {product[index]?.quantity}
                                  </Box>
                                  <Tooltip
                                    arrow
                                    placement="top"
                                    title={
                                      <Box
                                        sx={{
                                          bgcolor: "white",
                                          p: 1,
                                          borderRadius: "6px",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontSize: "10px",
                                            fontWeight: 600,
                                            color: "#373D3F",
                                            mb: 1,
                                          }}
                                        >
                                          Stock Details
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontSize: "10px",
                                            color: "#373D3F",
                                            fontWeight: 400,
                                          }}
                                        >
                                          Stock on Hand :{" "}
                                          {product[index]?.quantity || "0"}
                                        </Typography>
                                        {product[index]?.invoiceQuantity &&
                                          Number(
                                            product[index]?.invoiceQuantity
                                          ) >= 1000 && (
                                            <Typography
                                              sx={{
                                                fontSize: "10px",
                                                color: "#373D3F",
                                                fontWeight: 400,
                                                mt: 0.5,
                                              }}
                                            >
                                              Exact Quantity :{" "}
                                              {Number(
                                                product[index]?.invoiceQuantity
                                              ).toLocaleString()}
                                            </Typography>
                                          )}
                                      </Box>
                                    }
                                    componentsProps={{
                                      tooltip: {
                                        sx: {
                                          bgcolor: "white !important",
                                          p: 0,
                                          border:
                                            "1px solid #878787 !important",
                                          boxShadow: "none !important",
                                          "& .MuiTooltip-arrow": {
                                            color: "white !important",
                                            "&::before": {
                                              border: "1px solid #878787",
                                              backgroundColor:
                                                "white !important",
                                            },
                                          },
                                        },
                                      },
                                    }}
                                  >
                                    <IconButton
                                      size="small"
                                      edge="end"
                                      sx={{
                                        color: "#878787",
                                        padding: "4px",
                                      }}
                                    >
                                      <InfoOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={9} lg={1.9}>
                      <TextField
                        type="number"
                        placeholder="Amount"
                        value={
                          (Number(product[index]?.unit) || 0) *
                          (Number(product[index]?.invoiceQuantity) || 0)
                        }
                        disabled
                        fullWidth
                        // variant="outlined"
                        InputProps={{
                          readOnly: true,
                          sx: {
                            borderRadius: "8px",
                            my: 1,
                            fontSize: "14px",
                            fontWeight: "400",
                          },
                        }}
                      />
                    </Grid>
                    {index === 0 && (
                      <Grid item xs={1.5} lg={0.5}>
                        <Box sx={style.addIcon} onClick={handleAddField}>
                          +
                        </Box>
                      </Grid>
                    )}
                    {index !== 0 && (
                      <Grid item xs={1.5} lg={0.5}>
                        <Box
                          sx={style.deleteIcon}
                          onClick={() => handleDeleteField(index)}
                        >
                          <DeleteOutlineIcon />
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              );
            })}
          </Grid>

          {/* --- Discount and Tax */}
          <Grid container sx={style.lastGrid}>
            <Box sx={style.itemSection}>
              <Box
                sx={{
                  backgroundColor: "#28AA4A",
                  width: "2%",
                  height: 2,
                  marginLeft: "-16px",
                }}
              ></Box>
              <Typography sx={style.gridText}>Discount and Tax(%)</Typography>
            </Box>

            <Grid item xs={12} lg={7.6} sx={style.gridboxs}>
              <Grid container>
                <Box sx={{ ...style.inputSection, width: "100%", gap: 3.5 }}>
                  <Box
                    sx={{ ...style.inputBox, width: { xs: "100%", md: "50%" } }}
                  >
                    <Typography sx={style.inputText}>
                      Discount
                      {/* <span style={{ color: "red" }}>*</span> */}
                    </Typography>
                    <CustomInput
                      variant="outlined"
                      required
                      fullWidth
                      id="Discount"
                      placeholder="(Discount)"
                      error={discount?.error || ""}
                      type="number"
                      name="discount"
                      value={discount?.value}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      autoComplete="discount"
                    />
                  </Box>

                  <Box
                    sx={{ ...style.inputBox, width: { xs: "100%", md: "50%" } }}
                  >
                    <Typography sx={style.inputText}>Tax</Typography>
                    <CustomInput
                      variant="outlined"
                      required
                      fullWidth
                      type="number"
                      id="Tax"
                      placeholder="(Tax)"
                      error={tax?.error || ""}
                      name="tax"
                      value={tax?.value}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      autoComplete="tax"
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>

            <Grid
              item
              xs={12}
              lg={4.2}
              sx={{ ...style.gridboxs, marginTop: { md: "-17px !important" } }}
            >
              <Box sx={style.leaveSection}>
                <Box sx={style.inputContainerBox}>
                  <Box sx={style.key}>
                    <Typography sx={style.descValueText}>Total</Typography>
                  </Box>
                  <Box sx={style.key}>
                    <Typography sx={style.descValueText}>
                      {totalAmount}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={style.inputContainerBox}>
                  <Box sx={style.key}>
                    <Typography sx={style.descValueText}>Discount</Typography>
                  </Box>
                  <Box sx={style.key}>
                    <Typography sx={style.descValueText}>
                      -{discountValue || 0} ({discount?.value || 0}%)
                    </Typography>
                  </Box>
                </Box>
                <Box sx={style.inputContainerBox}>
                  <Box sx={style.key}>
                    <Typography sx={style.descValueText}>Tax</Typography>
                  </Box>
                  <Box sx={style.key}>
                    <Typography sx={style.descValueText}>
                      +{taxAmount || 0} ({tax?.value || 0}%)
                    </Typography>
                  </Box>
                </Box>
                <Box sx={style.inputContainerBox}>
                  <Box sx={style.key}>
                    <Typography sx={style.descValueTexts}>
                      Grand Total
                    </Typography>
                  </Box>
                  <Box sx={style.key}>
                    <Typography sx={style.descValueTexts}>
                      {finalAmount || 0}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Grid>

        <Box sx={style.mainBoxes}>
          <Box sx={style.createJob}>
            <Button
              variant="contained"
              color="success"
              disabled={!isFormValid()}
              type="submit"
              sx={style.cancelButtons}
            >
              Create Invoice
            </Button>
          </Box>
        </Box>
      </form>
    </>
  );
};

export default CreateInvoice;
